import { provideHttpClient } from '@angular/common/http';
import { isDevMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAuth0 } from '@auth0/auth0-angular';
import { provideIonicAngular } from '@ionic/angular/standalone';

import { routes } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { authConfig } from './app/core/models/auth.config';
import { AuthService } from './app/core/services/auth.service';
import { ConversationHistoryService } from './app/core/services/conversation-history.service';
import { TranslatorService } from './app/core/services/translator.service';
import { environment } from './environments/environment';

// Initialize Firestore EARLY - before Angular bootstraps
// This ensures emulator connection happens before any services try to use Firestore
import { initializeFirestore } from './app/core/services/firestore-init';
if (typeof window !== 'undefined') {
  console.log('[main.ts] 🔥 Initializing Firestore early (before Angular)...');
  try {
    initializeFirestore();
    console.log('[main.ts] ✅ Firestore initialized successfully');
  } catch (error) {
    console.error('[main.ts] ❌ Firestore initialization failed:', error);
  }
}

// Enhanced console.error filtering for Stripe errors (runs after index.html script)
// Also handle unhandled promise rejections for Ionic icon errors
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const errorMessage = error?.message || error?.toString() || String(error);
    
    if (
      errorMessage.includes('Failed to construct \'URL\'') ||
      errorMessage.includes('Invalid base URL') ||
      errorMessage.includes('getAssetPath') ||
      errorMessage.includes('icon.js')
    ) {
      event.preventDefault();
      return;
    }
  });
}

const originalConsoleError = console.error;
console.error = function (...args: any[]) {
  const errorMessage = args.join(' ');
  const firstArg = args[0];
  const secondArg = args[1];

  // Check if this is a Stripe analytics error (harmless)
  const isStripeAnalyticsError =
    (typeof errorMessage === 'string' &&
      (errorMessage.includes('r.stripe.com/b') ||
        errorMessage.includes('r.stripe.com') ||
        errorMessage.includes('POST https://r.stripe.com/b') ||
        errorMessage.includes('POST https://r.stripe.com') ||
        (errorMessage.includes('stripe') && errorMessage.includes('400')))) ||
    (typeof firstArg === 'string' &&
      (firstArg.includes('r.stripe.com/b') ||
        firstArg.includes('r.stripe.com') ||
        firstArg.includes('POST https://r.stripe.com'))) ||
    (firstArg &&
      typeof firstArg === 'object' &&
      firstArg.toString &&
      firstArg.toString().includes('r.stripe.com')) ||
    (secondArg &&
      typeof secondArg === 'string' &&
      secondArg.includes('r.stripe.com'));

  // Check if this is a harmless module resolution error
  const isModuleResolutionError =
    (typeof errorMessage === 'string' &&
      (errorMessage.includes("Cannot find module './en'") ||
        errorMessage.includes("Cannot find module './") ||
        errorMessage.includes('Cannot find module') ||
        errorMessage.includes('ChunkLoadError') ||
        errorMessage.includes('Loading chunk'))) ||
    (typeof firstArg === 'string' &&
      (firstArg.includes('Cannot find module') ||
        firstArg.includes('ChunkLoadError')));

  // Check if this is an Ionicons URL construction error (harmless - icons still work)
  const isIoniconsError =
    (typeof errorMessage === 'string' &&
      (errorMessage.includes('Failed to construct \'URL\'') ||
        errorMessage.includes('Invalid base URL') ||
        errorMessage.includes('getAssetPath') ||
        errorMessage.includes('icon.js'))) ||
    (typeof firstArg === 'string' &&
      (firstArg.includes('Failed to construct \'URL\'') ||
        firstArg.includes('Invalid base URL') ||
        firstArg.includes('getAssetPath'))) ||
    (firstArg &&
      typeof firstArg === 'object' &&
      firstArg?.stack &&
      typeof firstArg.stack === 'string' &&
      (firstArg.stack.includes('getAssetPath') ||
        firstArg.stack.includes('icon.js')));

  if (isStripeAnalyticsError || isModuleResolutionError || isIoniconsError) {
    return; // Silently ignore
  }

  // Call original console.error
  originalConsoleError.apply(console, args);
};

// Global console log control based on environment
if (!environment.enableConsoleLog) {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
  // Keep console.error for critical errors in production (but filtered)

  console.log('🔇 Console logging disabled (production mode)');
}

bootstrapApplication(AppComponent, {
  providers: [
    provideIonicAngular({
      mode: 'md',
    }),
    provideHttpClient(),
    provideRouter(routes),
    provideAuth0(authConfig),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode() && environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AuthService,
    ConversationHistoryService,
    TranslatorService,
  ],
}).catch((err) => console.log(err));
